<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted } from 'vue'
import DJIcon from '~/components/ui/icon/Icon.vue'
import { IconAlertCircle, IconCheck, IconInfo, IconWarning, IconX } from '~/components/ui/icon/icons'

// v2 §Banners & Toast / §Feedback & System — a single transient DARK toast. Duration comes
// from the store (success/info/warning 5s, errors STICKY=0); pausable on hover/focus,
// dismissible. role=status (polite) / role=alert for errors.
defineOptions({ name: 'DJToast' })

const props = defineProps({
  id: { type: Number, required: true },
  variant: { type: String, default: 'success' }, // 'success' | 'info' | 'warning' | 'error'
  message: { type: String, default: '' },
  duration: { type: Number, default: 5000 }, // ms; 0 = sticky (errors)
})
const emit = defineEmits<{ (e: 'dismiss', id: number): void }>()

// error = filled circle-! (circle-alert); warning = ⚠ triangle; success = ✓; info = i
const iconMap: Record<string, any> = { success: IconCheck, info: IconInfo, warning: IconWarning, error: IconAlertCircle }
const icon = computed(() => iconMap[props.variant] || IconCheck)
const role = computed(() => (props.variant === 'error' ? 'alert' : 'status'))

let timer: ReturnType<typeof setTimeout> | null = null
function start() {
  if (props.duration > 0) timer = setTimeout(() => emit('dismiss', props.id), props.duration)
}
function pause() {
  if (timer) {
    clearTimeout(timer)
    timer = null
  }
}
onMounted(start)
onBeforeUnmount(pause)
</script>

<template>
  <div
    class="dj-toast"
    :class="`dj-toast--${variant}`"
    :role="role"
    @mouseenter="pause"
    @mouseleave="start"
    @focusin="pause"
    @focusout="start"
  >
    <DJIcon :size="18" class="dj-toast__icon" aria-hidden="true"><component :is="icon" /></DJIcon>
    <span class="dj-toast__msg">{{ message }}</span>
    <button type="button" class="dj-toast__close" aria-label="Dismiss notification" @click="emit('dismiss', id)">
      <DJIcon :size="16"><IconX /></DJIcon>
    </button>
  </div>
</template>

<style scoped lang="scss">
.dj-toast {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 260px;
  max-width: 400px;
  padding: 12px 14px;
  border-radius: $radius-xl; // v2 12px
  background-color: $ink; // v2 transient DARK surface
  color: $cream;
  box-shadow: $elevation-toast;

  &__icon {
    flex-shrink: 0;
  }
  // lighter tint for legibility on the dark surface (icon = meaning, never colour alone)
  &--success .dj-toast__icon { color: $success-tint-strong; }
  &--info .dj-toast__icon { color: $info-tint-strong; }
  &--warning .dj-toast__icon { color: $gold-accent; } // v2 §Feedback — gold warning
  &--error .dj-toast__icon { color: $error-tint-strong; }

  &__msg {
    flex: 1;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
  }
  &__close {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border: none;
    border-radius: $radius-md;
    background: transparent;
    color: $cream;
    cursor: pointer;
    opacity: 0.7;
    transition: opacity $dur-fast $ease-out, background-color $dur-fast $ease-out;
    @include focus-ring;

    &:hover {
      opacity: 1;
      background-color: rgba($white, 0.12);
    }
  }
}
</style>
